import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useT } from '../hooks/useT';
import { useUpdateMe } from '../hooks/useAuth';
import { subscriptionService, authService } from '../services';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

const FREE_FEATURES = ['feature.free.1', 'feature.free.2', 'feature.free.3', 'feature.free.4'];
const PAID_FEATURES = [
  'feature.paid.1', 'feature.paid.2', 'feature.paid.3',
  'feature.paid.4', 'feature.paid.5', 'feature.paid.6',
];

export default function SettingsPage() {
  const t = useT();
  const { user, isPaid } = useAuthStore();

  /* ── Account form ─────────────────────────────────────────────────────── */
  const [name, setName] = useState(user?.full_name || '');
  const [accountMsg, setAccountMsg] = useState(null);
  const { mutateAsync: updateMe, isPending: savingAccount } = useUpdateMe();

  async function handleSaveAccount(e) {
    e.preventDefault();
    setAccountMsg(null);
    try {
      await updateMe({ full_name: name.trim() });
      setAccountMsg('ok');
      setTimeout(() => setAccountMsg(null), 3000);
    } catch {
      setAccountMsg('err');
    }
  }

  /* ── Password form ────────────────────────────────────────────────────── */
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);

  const { mutateAsync: changePassword, isPending: savingPw } = useMutation({
    mutationFn: (data) => authService.changePassword(data),
  });

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwMsg(null);
    if (pw.next !== pw.confirm) {
      setPwMsg('mismatch');
      return;
    }
    if (pw.next.length < 8) {
      setPwMsg('short');
      return;
    }
    try {
      await changePassword({ current_password: pw.current, new_password: pw.next });
      setPw({ current: '', next: '', confirm: '' });
      setPwMsg('ok');
      setTimeout(() => setPwMsg(null), 3000);
    } catch (err) {
      setPwMsg(err.response?.data?.error?.message || 'err');
    }
  }

  const { setUser } = useAuthStore();

  /* ── Subscription data ────────────────────────────────────────────────── */
  const { data: subStatus, isLoading: loadingSub } = useQuery({
    queryKey: ['subscription'],
    queryFn: subscriptionService.getStatus,
    onSuccess: (data) => {
      // Sync tier into the store so the rest of the app reflects paid/free immediately
      if (data?.tier && user?.tier !== data.tier) {
        setUser({ ...user, tier: data.tier });
      }
    },
  });

  const { mutateAsync: startCheckout, isPending: loadingCheckout } = useMutation({
    mutationFn: () =>
      subscriptionService.checkout({
        success_url: `${window.location.origin}/settings?upgraded=1`,
        cancel_url:  `${window.location.origin}/settings`,
      }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  const { mutateAsync: openPortal, isPending: loadingPortal } = useMutation({
    mutationFn: () =>
      subscriptionService.portal({ return_url: `${window.location.origin}/settings` }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  // Use live subscription data as the source of truth when available,
  // fallback to store while the query is loading.
  const paid = subStatus ? subStatus.tier === 'paid' : isPaid();
  const periodEnd = subStatus?.current_period_end
    ? new Date(subStatus.current_period_end).toLocaleDateString(undefined, {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;
  const cancelAtEnd = subStatus?.cancel_at_period_end;

  return (
    <div className="space-y-8 animate-fade-in max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-100">{t('settings.title')}</h1>
        <p className="text-slate-400 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* ── Account Information ──────────────────────────────────────────── */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-slate-200 text-lg">{t('settings.account')}</h2>

        <form onSubmit={handleSaveAccount} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={t('settings.name')}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <div>
              <label className="label">{t('settings.email')}</label>
              <p className="input bg-surface-hover/50 text-slate-400 cursor-default select-all">
                {user?.email}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={savingAccount}>
              {savingAccount ? t('settings.saving') : t('settings.save')}
            </Button>
            {accountMsg === 'ok' && (
              <span className="text-sm text-green-400">✓ {t('settings.saved')}</span>
            )}
            {accountMsg === 'err' && (
              <span className="text-sm text-red-400">Failed to save.</span>
            )}
          </div>
        </form>
      </div>

      {/* ── Change Password ──────────────────────────────────────────────── */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-slate-200 text-lg">{t('settings.password')}</h2>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <Input
            label={t('settings.current_password')}
            type="password"
            value={pw.current}
            onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
            required
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label={t('settings.new_password')}
              type="password"
              value={pw.next}
              onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
              required
            />
            <Input
              label={t('settings.confirm_password')}
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              required
            />
          </div>

          {pwMsg === 'mismatch' && (
            <p className="text-sm text-red-400">Passwords do not match.</p>
          )}
          {pwMsg === 'short' && (
            <p className="text-sm text-red-400">Password must be at least 8 characters.</p>
          )}
          {pwMsg === 'ok' && (
            <p className="text-sm text-green-400">✓ Password updated successfully.</p>
          )}
          {pwMsg && pwMsg !== 'ok' && pwMsg !== 'mismatch' && pwMsg !== 'short' && (
            <p className="text-sm text-red-400">{pwMsg}</p>
          )}

          <Button type="submit" disabled={savingPw}>
            {savingPw ? t('settings.saving') : t('settings.update_password')}
          </Button>
        </form>
      </div>

      {/* ── Subscription ────────────────────────────────────────────────── */}
      <div className="card space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-200 text-lg">{t('settings.subscription')}</h2>
          <span className={paid ? 'badge-paid' : 'badge-free'}>
            {paid ? t('settings.plan.paid') : t('settings.plan.free')}
          </span>
        </div>

        {loadingSub ? (
          <div className="h-24 bg-surface animate-pulse rounded-xl" />
        ) : (
          <>
            {/* Current plan details */}
            {paid && periodEnd && (
              <div className="rounded-xl bg-surface p-4 text-sm text-slate-400 space-y-1">
                <p>
                  {cancelAtEnd
                    ? `${t('settings.plan.cancels')}: `
                    : `${t('settings.plan.renews')}: `}
                  <span className="text-slate-200 font-medium">{periodEnd}</span>
                </p>
                {cancelAtEnd && (
                  <p className="text-amber-400 text-xs">
                    Your Pro features remain active until {periodEnd}.
                  </p>
                )}
              </div>
            )}

            {/* Feature list */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-surface p-4 space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {t('settings.features.free')}
                </p>
                <ul className="space-y-1.5">
                  {FREE_FEATURES.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className={paid ? 'text-slate-600' : 'text-green-400'}>✓</span>
                      {t(k)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`rounded-xl p-4 space-y-2 ${paid ? 'bg-brand-900/30 border border-brand-800' : 'bg-surface'}`}>
                <p className="text-xs font-semibold text-brand-400 uppercase tracking-wide">
                  {t('settings.features.paid')}
                </p>
                <ul className="space-y-1.5">
                  {PAID_FEATURES.map((k) => (
                    <li key={k} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className={paid ? 'text-brand-400' : 'text-slate-600'}>✓</span>
                      {t(k)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="flex gap-3 pt-1">
              {!paid ? (
                <Button
                  onClick={() => startCheckout()}
                  disabled={loadingCheckout}
                  className="btn-primary"
                >
                  {loadingCheckout ? '…' : `✦ ${t('settings.upgrade')}`}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => openPortal()}
                  disabled={loadingPortal}
                >
                  {loadingPortal ? '…' : t('settings.manage')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
